import React, { useState } from "react";
import { useParams } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import ReactJson from "react-json-view";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Stack,
  Tabs,
  Tab,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  LinearProgress,
  Chip,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";

import { getBotType, getResStatusString, isReqBlockedBySD } from "../../application/analytics/common";

import colors from "../../../../utils/colors";
import { getStatusLabel } from "./WAFEventView";
import { getBotEventDetectors } from "./botEventDrawerData";
import useAuth from "../../../../hooks/useAuth";
import useEvent from "../../../../hooks/user/useEvent";
import { outlinedButtonSx } from "../../../common/Button";

const requestTitleSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const requestValueSx =  { fontSize: 14, lineHeight: "25px", color: colors.neutral[800] };

const BotEventView = () => {
  const { eventId } = useParams();

  const { isAuthenticated } = useAuth();
  const { getBotEvent, botEvent, setErr } = useEvent();

  const [selectedTab, setSelectedTab] = useState("timeline");

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getBotEvent(eventId);
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getBotEvent, eventId]);

  const firedDetectors = getBotEventDetectors(botEvent);

  return (
    <>
      <Helmet title="Bot Event" />
      {botEvent === null && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack direction={"row"} alignItems={"center"} spacing={1} onClick={() => window.history.back()} sx={{ cursor: "pointer" }}>
            <LayerdIcon icon={DETECTION_ICONS.back} size={20} color="primaryLight" />
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              Bot Event
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              Activity log
            </Typography>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3}>
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              sx={{
                minHeight: "inherit",
                height: 32,
                "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
                "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
                "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
                "& button .MuiTypography-root": { color: colors.neutral[700] },
                "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
              }}
            >
              <Tab
                value={"timeline"}
                label={<Typography sx={ { fontSize: 14 }}>Timeline</Typography>}
                sx={{ width: 106 }}
              />
              <Tab
                value={"rawdata"}
                label={<Typography sx={ { fontSize: 14 }}>Raw data</Typography>}
                sx={{ width: 106 }}
              />
            </Tabs>
            <Button
              startIcon={<LayerdIcon icon={DETECTION_ICONS.download} size={16} color="neutralDark" />}
              sx={{ width: 128, height: 32, ...outlinedButtonSx }}
            >
              Download
            </Button>
          </Stack>
          {selectedTab === "timeline" && (
            <Stack spacing={6}>
              <Stack direction={"row"} alignItems={"center"} spacing={6} sx={{ py: 3 }}>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Host name
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    {botEvent?.host_name}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Source IP
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    <ReactCountryFlag
                      countryCode={botEvent?.country_iso_code}
                      svg
                      style={{
                        width: "26px",
                        height: "18px",
                      }}
                    />{" "}
                    {botEvent?.src_ip}
                  </Typography>
                </Stack>
              </Stack>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.request} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Reqeust
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={6}>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Path</Typography>
                        <Typography sx={requestValueSx}>{botEvent?.uri}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Method</Typography>
                        <Typography sx={requestValueSx}>{botEvent?.method}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Host</Typography>
                        <Typography sx={requestValueSx}>{botEvent?.host_name}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Status</Typography>
                        {getStatusLabel(getResStatusString(botEvent?.resStatus))}
                      </Stack>
                    </Stack>
                    <Box>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Stack direction={"row"} alignItems={"center"} spacing={2}>
                            <LayerdIcon icon={DETECTION_ICONS.header} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Headers
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                              JA3 Fingerprint
                            </Typography>
                            <Typography sx={requestValueSx}>{botEvent?.ja3_hash}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                              User Agent
                            </Typography>
                            <Typography sx={requestValueSx}>{botEvent?.ua}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                              Referer
                            </Typography>
                            <Typography sx={requestValueSx}>{botEvent?.referrer ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.wafDetection} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Bot detection
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={4}>
                      <Stack spacing={2}>
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                          <LayerdIcon icon={DETECTION_ICONS.requestBlocked} size={20} color="primary" />
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Request blocked
                          </Typography>
                        </Stack>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pl: 7 }}>
                          {isReqBlockedBySD(botEvent?.resStatus) ? "True" : "False"}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box sx={{ width: "100%" }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                              Bot Score
                            </Typography>
                            <Typography sx={requestValueSx}>{botEvent?.bot_score ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                              Bot Type
                            </Typography>
                            <Typography sx={requestValueSx}>{getBotType(botEvent?.bot_score) ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        {firedDetectors.length > 0 && (
                          <Grid item xs={12}>
                            <Stack spacing={1}>
                              <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                                Fired Detectors ({firedDetectors.length})
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {firedDetectors.map((det) => (
                                  <Chip
                                    key={det}
                                    size="small"
                                    label={det}
                                    sx={{
                                      height: 22,
                                      fontSize: 12,
                                      borderRadius: "11px",
                                      background: "#EEF2FF",
                                      color: "#3730A3",
                                      border: "1px solid #C7D2FE",
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          </Grid>
                        )}
                        {[
                          ["Action", botEvent?.action],
                          ["Action Reason", botEvent?.action_reason],
                          botEvent?.action_intended && botEvent.action_intended !== botEvent?.action ? ["Action Intended", botEvent.action_intended] : null,
                          botEvent?.ml_prob !== undefined && botEvent.ml_prob !== 0 ? ["ML Probability", Number(botEvent.ml_prob).toFixed(3)] : null,
                          ["ML Model", botEvent?.ml_model_version],
                          ["FP Verdict", botEvent?.fp_verdict],
                          botEvent?.fp_confidence !== undefined && botEvent.fp_confidence !== 0 ? ["FP Confidence", Number(botEvent.fp_confidence).toFixed(3)] : null,
                          botEvent?.shadow_score !== undefined && botEvent.shadow_score !== 0 ? ["Shadow Score", botEvent.shadow_score] : null,
                          ["Shadow Type", botEvent?.shadow_type],
                          ["BM Engine Version", botEvent?.bm_engine_version],
                          ["Aggregator Version", botEvent?.aggregator_version],
                        ].filter(Boolean).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([label, value]) => (
                          <Grid item xs={6} key={label}>
                            <Stack spacing={1}>
                              <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                                {label}
                              </Typography>
                              <Typography sx={requestValueSx}>{String(value)}</Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.response} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Response
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Box>
                      <Stack direction={"row"} spacing={6}>
                        <Stack
                          spacing={2}
                          sx={{
                            border: `solid 1px ${isReqBlockedBySD(botEvent?.resStatus) ? colors.red[100] : colors.primary[300]}`,
                            backgroundColor: isReqBlockedBySD(botEvent?.resStatus) ? colors.red["10"] : colors.primary["alpha"],
                            borderRadius: "8px",
                            px: 4,
                            py: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Blocked By Layerd AI
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: isReqBlockedBySD(botEvent?.resStatus) ? colors.red[100] : colors.primary[300],
                            }}
                          >
                            {isReqBlockedBySD(botEvent?.resStatus) ? "True" : "False"}
                          </Typography>
                        </Stack>
                        <Stack spacing={2} sx={{ px: 4, py: 2 }}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <LayerdIcon icon={DETECTION_ICONS.responseStatus} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Response
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: colors.primary[300],
                              pl: 7,
                            }}
                          >
                            {botEvent?.resStatus}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
          {selectedTab === "rawdata" && (
            <Box>
              <ReactJson
                name="raw"
                src={botEvent?.raw}
                displayDataTypes={false}
                style={{ wordBreak: "break-word", fontSize: 13 }}
              />
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
};
export default BotEventView;
